// src/SparepartsPages/Dpo/DpoDashboard.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  Drawer,
  Tabs,
  Tab,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ Excel export
// npm i xlsx
import * as XLSX from "xlsx";

/* helpers */
const fmt = (v) => (v ? String(v).slice(0, 10) : "");
const num = (v) => (v === null || v === undefined ? 0 : Number(v) || 0);

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const StatusChip = ({ v }) => {
  if (!v) return <Chip size="small" label="-" />;
  const color =
    v === "ALL_PRESENT"
      ? "success"
      : String(v).startsWith("MISSING_")
        ? "error"
        : "secondary";
  return <Chip size="small" color={color} label={v} />;
};

const PresentChip = ({ ok, label }) => (
  <Chip
    size="small"
    sx={{ mr: 0.5, mb: 0.5 }}
    color={ok ? "success" : "error"}
    variant={ok ? "outlined" : "filled"}
    label={ok ? label : `MISSING ${label}`}
  />
);

const DpoDashboard = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const isLoggedIn = !!userToken;

  // ✅ avoid multiple timeouts on every render
  useEffect(() => {
    const t = setTimeout(() => window.location.reload(false), 1800000);
    return () => clearTimeout(t);
  }, []);

  // ✅ Main tabs (Summary/Detail)
  const [mainTab, setMainTab] = useState(0); // 0=Summary, 1=Detail

  /* shared search (optional) */
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);
  const searchPending = search !== debouncedSearch;

  /* =========================
     SUMMARY FILTERS + GRID
     ========================= */
  const [purchased, setPurchased] = useState("ALL"); // ALL|YES|NO|PENDING
  const [status, setStatus] = useState("ALL"); // ALL | ALL_PRESENT | MISSING_MAP | ...
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [gridLoading, setGridLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setSortModel] = useState([
    { field: "last_activity", sort: "desc" },
  ]);

  /* invoice drawer */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [activeInv, setActiveInv] = useState(null);

  const [dposLoading, setDposLoading] = useState(false);
  const [dpos, setDpos] = useState([]);

  const [trItemsLoading, setTrItemsLoading] = useState(false);
  const [trItems, setTrItems] = useState([]);

  const [csItemsLoading, setCsItemsLoading] = useState(false);
  const [csItems, setCsItems] = useState([]);

  /* ✅ DPO tab search (client-side, debounced) */
  const [dpoSearch, setDpoSearch] = useState("");
  const dpoSearchDeb = useDebouncedValue(dpoSearch, 250);
  const dpoSearchPending = dpoSearch !== dpoSearchDeb;

  /* dpo modal */
  const [dpoModalOpen, setDpoModalOpen] = useState(false);
  const [activeDpo, setActiveDpo] = useState(null);
  const [dpoItemsLoading, setDpoItemsLoading] = useState(false);
  const [dpoItems, setDpoItems] = useState([]);

  /* ✅ DPO items search (client-side, debounced) */
  const [dpoItemSearch, setDpoItemSearch] = useState("");
  const dpoItemSearchDeb = useDebouncedValue(dpoItemSearch, 250);
  const dpoItemSearchPending = dpoItemSearch !== dpoItemSearchDeb;

  /* =========================
     DETAIL (FETCH ALL ONCE, FRONTEND FILTER)
     Backend MUST return ALL rows without requiring invno/dpono
     GET /dpo/dashboard/detail-items-all
     ========================= */
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailAllRows, setDetailAllRows] = useState([]);

  // filter type: Invoice-wise or DPO-wise
  const [detailMode, setDetailMode] = useState("INV"); // INV | DPO
  const [detailInvNo, setDetailInvNo] = useState("");
  const [detailDpoNo, setDetailDpoNo] = useState("");

  // ✅ Detail tab filters (frontend)
  const [detailPurchased, setDetailPurchased] = useState("ALL"); // ALL|YES|NO|PENDING
  const [detailStatus, setDetailStatus] = useState("ALL"); // ALL|OK|PURCHASE_PENDING|NO_PURCHASE|others

  const detailInvNoDeb = useDebouncedValue(detailInvNo, 250);
  const detailDpoNoDeb = useDebouncedValue(detailDpoNo, 250);

  const fetchJSON = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        ...options,
        headers: { sptoken: userToken, ...(options.headers || {}) },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Request failed");
      return data;
    },
    [userToken],
  );

  const baseQS = useMemo(() => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set("search", debouncedSearch);

    // backend supports ALL|YES|NO only; PENDING is handled in frontend
    if (purchased !== "PENDING") p.set("purchased", purchased);

    // ✅ status filter
    if (status && status !== "ALL") p.set("status", status);

    return p.toString();
  }, [debouncedSearch, purchased, status]);

  // ✅ abort stale summary requests
  const gridAbortRef = useRef(null);

  const loadGrid = useCallback(async () => {
    if (gridAbortRef.current) gridAbortRef.current.abort();
    const controller = new AbortController();
    gridAbortRef.current = controller;

    setGridLoading(true);
    try {
      const sm = sortModel?.[0] || { field: "last_activity", sort: "desc" };

      if (purchased === "PENDING") {
        const chunkSize = 200;
        const allPending = [];
        let backendPage = 0;
        let totalNoRows = 0;

        while (true) {
          const pendingQS = new URLSearchParams(baseQS);
          pendingQS.set("purchased", "NO");
          pendingQS.set("page", backendPage);
          pendingQS.set("pageSize", chunkSize);
          pendingQS.set("sortBy", sm.field || "last_activity");
          pendingQS.set("sortDir", sm.sort || "desc");

          const pendingData = await fetchJSON(
            `/dpo/dashboard/invlist?${pendingQS.toString()}`,
            { signal: controller.signal },
          );

          const batchRows = Array.isArray(pendingData.rows)
            ? pendingData.rows
            : [];
          totalNoRows = Number(pendingData.rowCount || 0);

          if (!batchRows.length) break;

          for (const r of batchRows) {
            if (r?.purchase_pending) allPending.push(r);
          }

          backendPage += 1;
          if (backendPage * chunkSize >= totalNoRows) break;
        }

        const start = page * pageSize;
        setRows(allPending.slice(start, start + pageSize));
        setRowCount(allPending.length);
        return;
      }

      const p = new URLSearchParams(baseQS);
      p.set("page", page);
      p.set("pageSize", pageSize);
      p.set("sortBy", sm.field || "last_activity");
      p.set("sortDir", sm.sort || "desc");

      const data = await fetchJSON(`/dpo/dashboard/invlist?${p.toString()}`, {
        signal: controller.signal,
      });

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setRowCount(Number(data.rowCount || 0));
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e.message);
    } finally {
      if (gridAbortRef.current === controller) setGridLoading(false);
    }
  }, [baseQS, fetchJSON, page, pageSize, purchased, sortModel]);

  const apply = useCallback(async () => {
    setPage(0);
    await loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (mainTab !== 0) return;
    loadGrid();
  }, [loadGrid, isLoggedIn, mainTab]);

  /* =========================
     DETAIL: fetch ALL rows once
     ========================= */
  const detailAbortRef = useRef(null);

  const loadDetailAll = useCallback(async () => {
    if (detailAbortRef.current) detailAbortRef.current.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;

    setDetailLoading(true);
    try {
      const p = new URLSearchParams();
      if (debouncedSearch) p.set("search", debouncedSearch);

      // frontend wants ALL rows; backend may support huge pageSize
      p.set("page", 0);
      p.set("pageSize", 300000);

      const data = await fetchJSON(
        `/dpo/dashboard/detail-items-all?${p.toString()}`,
        {
          signal: controller.signal,
        },
      );

      setDetailAllRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e.message);
    } finally {
      if (detailAbortRef.current === controller) setDetailLoading(false);
    }
  }, [debouncedSearch, fetchJSON]);

  const applyDetail = useCallback(async () => {
    await loadDetailAll();
  }, [loadDetailAll]);

  // ✅ FRONTEND FILTER for Detail
  const detailFilteredRows = useMemo(() => {
    const src = Array.isArray(detailAllRows) ? detailAllRows : [];

    const invQ = String(detailInvNoDeb || "")
      .trim()
      .toLowerCase();
    const dpoQ = String(detailDpoNoDeb || "")
      .trim()
      .toLowerCase();

    // 1) Invoice/DPO filter (if empty, show ALL)
    let out = src;
    if (detailMode === "INV") {
      if (invQ) {
        out = out.filter((r) =>
          String(r?.invoiceno || r?.invno || "")
            .toLowerCase()
            .includes(invQ),
        );
      }
    } else {
      if (dpoQ) {
        out = out.filter((r) =>
          String(r?.dpono || "")
            .toLowerCase()
            .includes(dpoQ),
        );
      }
    }

    // 2) Purchased filter
    if (detailPurchased !== "ALL") {
      if (detailPurchased === "PENDING") {
        out = out.filter((r) =>
          String(r?.status || "")
            .toUpperCase()
            .includes("PENDING"),
        );
      } else if (detailPurchased === "YES") {
        out = out.filter(
          (r) =>
            !!r?.purchased &&
            !String(r?.status || "")
              .toUpperCase()
              .includes("PENDING"),
        );
      } else if (detailPurchased === "NO") {
        out = out.filter(
          (r) =>
            !r?.purchased &&
            !String(r?.status || "")
              .toUpperCase()
              .includes("PENDING"),
        );
      }
    }

    // 3) Status filter (exact, but ALL passes)
    if (detailStatus !== "ALL") {
      out = out.filter(
        (r) =>
          String(r?.status || "").toUpperCase() ===
          String(detailStatus).toUpperCase(),
      );
    }

    return out;
  }, [
    detailAllRows,
    detailInvNoDeb,
    detailDpoNoDeb,
    detailMode,
    detailPurchased,
    detailStatus,
  ]);

  const exportDetailExcel = useCallback(() => {
    try {
      const dataRows = Array.isArray(detailFilteredRows)
        ? detailFilteredRows
        : [];
      if (!dataRows.length) {
        toast.error("No rows to export (check Invoice/DPO filter)");
        return;
      }

      const flat = dataRows.map((r) => ({
        invoiceno: r.invoiceno ?? r.invno ?? "",
        invoicedate: fmt(r.invoicedate ?? r.invdate),
        inwardno: r.inwardno ?? "",
        inwarddate: r.inwarddate ?? "",
        dpono: r.dpono ?? "",
        itemcode: r.itemcode ?? r.partno ?? "",
        itemname: r.itemname ?? "",
        invqty: num(r.invqty),
        purchased: r.purchased ? "YES" : "NO",
        status: r.status ?? "",
      }));

      const ws = XLSX.utils.json_to_sheet(flat);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Detail");

      // ✅ filename requested: dpo + invoiceno + date
      const first = dataRows[0] || {};

      // ✅ filename requested: dpo + invoiceno + date
      const inv =
        detailMode === "INV" && String(detailInvNo || "").trim()
          ? String(detailInvNo || "").trim()
          : first.invoiceno || first.invno || "";
      const dpo =
        detailMode === "DPO" && String(detailDpoNo || "").trim()
          ? String(detailDpoNo || "").trim()
          : first.dpono || "";
      const dt =
        fmt(first.invoicedate || first.invdate) ||
        new Date().toISOString().slice(0, 10);

      const safe = (s) =>
        String(s || "")
          .replace(/[^\w\-]+/g, "_")
          .slice(0, 60);
      const filename = `DPO_DETAIL_${safe(dpo || "MULTI")}_${safe(inv || "MULTI")}_${safe(dt)}.xlsx`;

      XLSX.writeFile(wb, filename);
      toast.success("Excel exported");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
  }, [detailFilteredRows, detailInvNo, detailDpoNo]);

  // ✅ abort stale invoice open requests (fast click)
  const invAbortRef = useRef(null);

  const openInvoice = useCallback(
    async (row) => {
      const invNo = row?.invno || row?.invoiceno;
      if (!invNo) return;

      if (invAbortRef.current) invAbortRef.current.abort();
      const controller = new AbortController();
      invAbortRef.current = controller;

      // normalize activeInv for header chips
      setActiveInv((prev) => ({
        ...(prev || {}),
        invno: invNo,
        invdate: row?.invdate || row?.invoicedate || prev?.invdate,
        inwardno: row?.inwardno || prev?.inwardno,
        inwarddate: row?.inwarddate || prev?.inwarddate,
        purchased: row?.purchased ?? prev?.purchased,
        status: row?.status || prev?.status,
        has_map: prev?.has_map,
        has_transit: prev?.has_transit,
        has_case: prev?.has_case,
        purchase_pending: prev?.purchase_pending,
      }));

      setDrawerOpen(true);
      setDrawerTab(0);

      // reset drawer states
      setDpoSearch("");
      setDpos([]);
      setTrItems([]);
      setCsItems([]);

      setDposLoading(true);
      setTrItemsLoading(true);
      setCsItemsLoading(true);

      try {
        const inv = encodeURIComponent(invNo);
        const [dpoRes, trRes, csRes] = await Promise.all([
          fetchJSON(`/dpo/dashboard/invoice/${inv}/dpos`, {
            signal: controller.signal,
          }),
          fetchJSON(`/dpo/dashboard/invoice/${inv}/transit-items`, {
            signal: controller.signal,
          }),
          fetchJSON(`/dpo/dashboard/invoice/${inv}/case-items`, {
            signal: controller.signal,
          }),
        ]);

        setDpos(dpoRes.rows || []);
        setTrItems(trRes.rows || []);
        setCsItems(csRes.rows || []);
      } catch (e) {
        if (e?.name !== "AbortError") toast.error(e.message);
      } finally {
        if (invAbortRef.current === controller) {
          setDposLoading(false);
          setTrItemsLoading(false);
          setCsItemsLoading(false);
        }
      }
    },
    [fetchJSON],
  );

  // ✅ abort stale dpo items fetch
  const dpoAbortRef = useRef(null);

  const openDpoModal = useCallback(
    async (dpoRow) => {
      const dpono = String(dpoRow?.dpono || "").trim();
      const invno = String(activeInv?.invno || dpoRow?.invno || "").trim();

      if (!dpono) return;
      if (!invno) {
        toast.error("Invoice no missing");
        return;
      }

      if (dpoAbortRef.current) dpoAbortRef.current.abort();
      const controller = new AbortController();
      dpoAbortRef.current = controller;

      setActiveDpo(dpoRow);
      setDpoModalOpen(true);

      setDpoItemSearch("");
      setDpoItems([]);
      setDpoItemsLoading(true);

      try {
        const r = await fetchJSON(`/dpo/dashboard/dpo/items`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ dpono, invno }),
          signal: controller.signal,
        });
        setDpoItems(
          Array.isArray(r.rows)
            ? r.rows.map((x, i) => ({ ...x, __rid: `${x?.partno || ""}-${i}` }))
            : [],
        );
      } catch (e) {
        if (e?.name !== "AbortError") toast.error(e.message);
      } finally {
        if (dpoAbortRef.current === controller) setDpoItemsLoading(false);
      }
    },
    [fetchJSON, activeInv],
  );

  // ✅ memoized columns
  const invCols = useMemo(
    () => [
      {
        field: "invno",
        headerName: "Invoice No",
        minWidth: 220,
        flex: 1,
        renderCell: (p) => (
          <Button size="small" onClick={() => openInvoice(p.row)}>
            {p.value}
          </Button>
        ),
      },
      {
        field: "invdate",
        headerName: "Inv Date",
        minWidth: 110,
        valueFormatter: (p) => fmt(p.value),
      },
      { field: "inwardno", headerName: "Inward No", minWidth: 220, flex: 1 },
      {
        field: "inwarddate",
        headerName: "Inward Date",
        minWidth: 160,
        flex: 1,
      },
      {
        field: "purchased",
        headerName: "Purchased",
        minWidth: 110,
        renderCell: (p) =>
          p.row?.purchase_pending ? (
            <Chip size="small" color="warning" label="PENDING" />
          ) : p.value ? (
            <Chip size="small" color="success" label="YES" />
          ) : (
            <Chip size="small" label="NO" />
          ),
      },
      {
        field: "status",
        headerName: "Status (Map/Transit/Case)",
        minWidth: 260,
        renderCell: (p) => <StatusChip v={p.value} />,
      },
      {
        field: "presence",
        headerName: "Presence",
        minWidth: 420,
        sortable: false,
        renderCell: (p) => (
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            <PresentChip ok={!!p.row.has_map} label="DpoInvMap" />
            <PresentChip ok={!!p.row.has_transit} label="Transit" />
            <PresentChip ok={!!p.row.has_case} label="Case" />
          </Box>
        ),
      },
    ],
    [openInvoice],
  );

  const dpoCols = useMemo(
    () => [
      { field: "dpono", headerName: "DPO", minWidth: 260, flex: 1 },
      {
        field: "dpodate",
        headerName: "DPO Date",
        minWidth: 110,
        valueFormatter: (p) => fmt(p.value),
      },
      {
        field: "partyname",
        headerName: "Party",
        minWidth: 260,
        flex: 1,
        renderCell: (p) =>
          p.row.isstock ? (
            <Chip size="small" label="STOCK" />
          ) : (
            <Chip size="small" color="success" label={p.value || "STOCK"} />
          ),
      },
      {
        field: "status",
        headerName: "Mapped?",
        minWidth: 140,
        renderCell: (p) =>
          p.value === "DPO_UPLOADED" ? (
            <Chip size="small" color="success" label="MAPPED" />
          ) : (
            <Chip size="small" color="error" label="NOT MAPPED" />
          ),
      },
      {
        field: "action",
        headerName: "Open",
        minWidth: 110,
        sortable: false,
        renderCell: (p) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openDpoModal(p.row)}
          >
            View
          </Button>
        ),
      },
    ],
    [openDpoModal],
  );

  const trCols = useMemo(
    () => [
      { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
      { field: "itemname", headerName: "Item", minWidth: 260, flex: 1.2 },
      { field: "qty", headerName: "Qty", width: 110, type: "number" },
      { field: "value", headerName: "Value", width: 130, type: "number" },
    ],
    [],
  );

  const csCols = useMemo(
    () => [
      { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
      { field: "itemname", headerName: "Item", minWidth: 300, flex: 1.4 },
      { field: "qty", headerName: "Qty", width: 110, type: "number" },
      { field: "racknames", headerName: "Rack", minWidth: 220, flex: 1 },
    ],
    [],
  );

  const dpoItemCols = useMemo(
    () => [
      { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
      { field: "itemname", headerName: "Item", minWidth: 260, flex: 1.3 },
      { field: "dpo_qty", headerName: "Inv Qty", width: 120, type: "number" },
    ],
    [],
  );

  const detailCols = useMemo(
    () => [
      {
        field: "invoiceno",
        headerName: "Invoice No",
        minWidth: 220,
        flex: 1,
        renderCell: (p) => (
          <Button size="small" onClick={() => openInvoice(p.row)}>
            {p.value}
          </Button>
        ),
      },
      {
        field: "invoicedate",
        headerName: "Inv Date",
        minWidth: 110,
        valueFormatter: (p) => fmt(p.value),
      },
      { field: "inwardno", headerName: "Inward No", minWidth: 220, flex: 1 },
      {
        field: "inwarddate",
        headerName: "Inward Date",
        minWidth: 160,
        flex: 1,
      },
      { field: "dpono", headerName: "DPO", minWidth: 220, flex: 1 },
      { field: "itemcode", headerName: "Item Code", minWidth: 160, flex: 1 },
      { field: "itemname", headerName: "Item Name", minWidth: 260, flex: 1.4 },
      { field: "invqty", headerName: "Inv Qty", width: 120, type: "number" },
      {
        field: "purchased",
        headerName: "Purchased",
        minWidth: 120,
        renderCell: (p) =>
          p.value ? (
            <Chip size="small" color="success" label="YES" />
          ) : (
            <Chip size="small" label="NO" />
          ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 220,
        renderCell: (p) => <StatusChip v={p.value} />,
      },
    ],
    [openInvoice],
  );

  const filteredDpos = useMemo(() => {
    const q = String(dpoSearchDeb || "")
      .trim()
      .toLowerCase();
    if (!q) return dpos;

    return (Array.isArray(dpos) ? dpos : []).filter((r) => {
      const dpono = String(r?.dpono || "").toLowerCase();
      const party = String(r?.partyname || "").toLowerCase();
      const statusTxt = String(r?.status || "").toLowerCase();
      return dpono.includes(q) || party.includes(q) || statusTxt.includes(q);
    });
  }, [dpos, dpoSearchDeb]);

  const filteredDpoItems = useMemo(() => {
    const q = String(dpoItemSearchDeb || "")
      .trim()
      .toLowerCase();
    if (!q) return dpoItems;

    return (Array.isArray(dpoItems) ? dpoItems : []).filter((r) => {
      const part = String(r?.partno || "").toLowerCase();
      const item = String(r?.itemname || "").toLowerCase();
      return part.includes(q) || item.includes(q);
    });
  }, [dpoItems, dpoItemSearchDeb]);

  const dpoSearchAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <Stack direction="row" spacing={1} alignItems="center">
          {(dposLoading || dpoSearchPending) && <CircularProgress size={16} />}
          <Typography
            variant="caption"
            sx={{ minWidth: 44, textAlign: "right" }}
          >
            {filteredDpos.length}
          </Typography>
        </Stack>
      </InputAdornment>
    ),
    [dposLoading, dpoSearchPending, filteredDpos.length],
  );

  const dpoItemSearchAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <Stack direction="row" spacing={1} alignItems="center">
          {(dpoItemsLoading || dpoItemSearchPending) && (
            <CircularProgress size={16} />
          )}
          <Typography
            variant="caption"
            sx={{ minWidth: 44, textAlign: "right" }}
          >
            {filteredDpoItems.length}
          </Typography>
        </Stack>
      </InputAdornment>
    ),
    [dpoItemsLoading, dpoItemSearchPending, filteredDpoItems.length],
  );

  // cleanup: abort inflight requests
  useEffect(() => {
    return () => {
      try {
        gridAbortRef.current?.abort?.();
        detailAbortRef.current?.abort?.();
        invAbortRef.current?.abort?.();
        dpoAbortRef.current?.abort?.();
      } catch {
        // ignore
      }
    };
  }, []);

  if (!isLoggedIn) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2000} newestOnTop />
      <Sidebar
        Title={"Invoice Dashboard"}
        expandedPanel={"panel2"}
        children={
          <Box sx={{ p: 1 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                sx={{
                  py: 1,
                  "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 700 },
                }}
                title="Transit Invoice Dashboard (linked with DpoInvMap + Case)"
                subheader={
                  <Typography variant="caption">
                    Summary = Transit invoice list • Detail = Fetch ALL once,
                    then filter by Invoice/DPO and export Excel
                  </Typography>
                }
              />
              <Divider />
              <CardContent sx={{ p: 1 }}>
                {/* TOP SEARCH BAR (shared) */}
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={10}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Search (optional) Invoice / DPO / Party"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (mainTab === 0) apply();
                          else applyDetail();
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {searchPending || gridLoading || detailLoading ? (
                              <CircularProgress size={18} />
                            ) : null}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => (mainTab === 0 ? apply() : applyDetail())}
                      disabled={gridLoading || detailLoading}
                      fullWidth
                    >
                      Apply
                    </Button>
                  </Grid>
                </Grid>

                {/* ✅ MAIN TABS */}
                <Divider sx={{ my: 1 }} />
                <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)}>
                  <Tab label="Summary" />
                  <Tab label="Detail" />
                </Tabs>

                {/* ========================= SUMMARY TAB ========================= */}
                {mainTab === 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Purchased"
                          value={purchased}
                          onChange={(e) => {
                            setPurchased(e.target.value);
                            setPage(0);
                          }}
                        >
                          <MenuItem value="ALL">ALL</MenuItem>
                          <MenuItem value="YES">YES</MenuItem>
                          <MenuItem value="NO">NO</MenuItem>
                          <MenuItem value="PENDING">PENDING</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Status"
                          value={status}
                          onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(0);
                          }}
                        >
                          <MenuItem value="ALL">ALL</MenuItem>
                          <MenuItem value="ALL_PRESENT">ALL_PRESENT</MenuItem>
                          <MenuItem value="MISSING_MAP">MISSING_MAP</MenuItem>
                          <MenuItem value="MISSING_TRANSIT">
                            MISSING_TRANSIT
                          </MenuItem>
                          <MenuItem value="MISSING_CASE">MISSING_CASE</MenuItem>
                          <MenuItem value="OTHER">OTHER</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ height: 620 }}>
                      <DataGrid
                        rows={rows}
                        columns={invCols}
                        getRowId={(r) => r.invno}
                        density="compact"
                        rowHeight={32}
                        loading={gridLoading}
                        rowCount={rowCount}
                        pagination
                        paginationMode="server"
                        page={page}
                        pageSize={pageSize}
                        onPageChange={(p) => setPage(p)}
                        onPageSizeChange={(s) => {
                          setPageSize(s);
                          setPage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={(m) => setSortModel(m)}
                        disableRowSelectionOnClick
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                          toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 300 },
                          },
                        }}
                      />
                    </Box>
                  </>
                )}

                {/* ========================= DETAIL TAB =========================
{/* ========================= DETAIL TAB ========================= */}
                {mainTab === 1 && (
                  <>
                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Filter Mode"
                          value={detailMode}
                          onChange={(e) => setDetailMode(e.target.value)}
                        >
                          <MenuItem value="INV">Invoice wise</MenuItem>
                          <MenuItem value="DPO">DPO wise</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        {detailMode === "INV" ? (
                          <TextField
                            size="small"
                            fullWidth
                            label="Invoice No (frontend filter)"
                            value={detailInvNo}
                            onChange={(e) => setDetailInvNo(e.target.value)}
                          />
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            label="DPO No (frontend filter)"
                            value={detailDpoNo}
                            onChange={(e) => setDetailDpoNo(e.target.value)}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Purchased"
                          value={detailPurchased}
                          onChange={(e) => setDetailPurchased(e.target.value)}
                        >
                          <MenuItem value="ALL">ALL</MenuItem>
                          <MenuItem value="YES">YES</MenuItem>
                          <MenuItem value="NO">NO</MenuItem>
                          <MenuItem value="PENDING">PENDING</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Status"
                          value={detailStatus}
                          onChange={(e) => setDetailStatus(e.target.value)}
                        >
                          <MenuItem value="ALL">ALL</MenuItem>
                          <MenuItem value="OK">OK</MenuItem>
                          <MenuItem value="PURCHASE_PENDING">
                            PURCHASE_PENDING
                          </MenuItem>
                          <MenuItem value="NO_PURCHASE">NO_PURCHASE</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          flexWrap="wrap"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setDetailInvNo("");
                              setDetailDpoNo("");
                              setDetailPurchased("ALL");
                              setDetailStatus("ALL");
                            }}
                          >
                            Clear Filter
                          </Button>

                          <Button
                            size="small"
                            variant="contained"
                            onClick={applyDetail}
                            disabled={detailLoading}
                          >
                            Fetch ALL
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            onClick={exportDetailExcel}
                            disabled={
                              detailLoading || !detailFilteredRows.length
                            }
                          >
                            Export Excel
                          </Button>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ alignSelf: "center" }}
                          >
                            All: {detailAllRows.length} • Filtered:{" "}
                            {detailFilteredRows.length}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ height: 620 }}>
                      <DataGrid
                        rows={detailFilteredRows}
                        columns={detailCols}
                        getRowId={(r) =>
                          `${r.invoiceno || r.invno || ""}__${r.dpono || ""}__${r.itemcode || ""}__${r.itemid || ""}`
                        }
                        density="compact"
                        rowHeight={32}
                        loading={detailLoading}
                        disableRowSelectionOnClick
                        pagination
                        pageSizeOptions={[10, 20, 50, 100]}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 20, page: 0 },
                          },
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                          toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 300 },
                          },
                        }}
                      />
                    </Box>
                  </>
                )}

                {/* Invoice Drawer (unchanged) */}
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{ sx: { width: { xs: "100%", md: 920 } } }}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                          Invoice: {activeInv?.invno || "-"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          sx={{ mt: 0.5 }}
                        >
                          <Chip
                            size="small"
                            label={`InvDate: ${fmt(activeInv?.invdate) || "-"}`}
                          />
                          <Chip
                            size="small"
                            label={`Purchased: ${
                              activeInv?.purchase_pending
                                ? "PENDING"
                                : activeInv?.purchased
                                  ? "YES"
                                  : "NO"
                            }`}
                          />
                          <StatusChip v={activeInv?.status} />
                          <PresentChip
                            ok={!!activeInv?.has_map}
                            label="DpoInvMap"
                          />
                          <PresentChip
                            ok={!!activeInv?.has_transit}
                            label="Transit"
                          />
                          <PresentChip
                            ok={!!activeInv?.has_case}
                            label="Case"
                          />
                        </Stack>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDrawerOpen(false)}
                      >
                        Close
                      </Button>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Tabs
                      value={drawerTab}
                      onChange={(e, v) => setDrawerTab(v)}
                    >
                      <Tab label="DPOs" />
                      <Tab label="Transit Items" />
                      <Tab label="Case Items" />
                    </Tabs>

                    {drawerTab === 0 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Search DPO / Party / Status"
                            value={dpoSearch}
                            onChange={(e) => setDpoSearch(e.target.value)}
                            InputProps={{ endAdornment: dpoSearchAdornment }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setDpoSearch("")}
                            disabled={!dpoSearch}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Clear
                          </Button>
                        </Stack>

                        <DataGrid
                          rows={filteredDpos}
                          getRowId={(r) => r.dpono}
                          columns={dpoCols}
                          density="compact"
                          rowHeight={32}
                          disableRowSelectionOnClick
                          loading={dposLoading}
                          slots={{ toolbar: GridToolbar }}
                          slotProps={{ toolbar: { showQuickFilter: false } }}
                        />
                      </Box>
                    )}

                    {drawerTab === 1 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {trItemsLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DataGrid
                            rows={trItems}
                            getRowId={(r) => r.partno}
                            columns={trCols}
                            density="compact"
                            rowHeight={32}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                              toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {drawerTab === 2 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {csItemsLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DataGrid
                            rows={csItems}
                            getRowId={(r) => r.partno}
                            columns={csCols}
                            density="compact"
                            rowHeight={32}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                              toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Drawer>

                {/* DPO Modal */}
                <Dialog
                  open={dpoModalOpen}
                  onClose={() => setDpoModalOpen(false)}
                  maxWidth="lg"
                  fullWidth
                >
                  <DialogTitle sx={{ pr: 6 }}>
                    DPO: {activeDpo?.dpono || "-"}
                    <IconButton
                      onClick={() => setDpoModalOpen(false)}
                      sx={{ position: "absolute", right: 10, top: 10 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>

                  <DialogContent dividers>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        size="small"
                        label={`DPO Date: ${fmt(activeDpo?.dpodate) || "-"}`}
                      />
                      <Chip
                        size="small"
                        label={`Party: ${activeDpo?.isstock ? "STOCK" : activeDpo?.partyname || "STOCK"}`}
                      />
                      <Chip
                        size="small"
                        color={
                          activeDpo?.status === "DPO_UPLOADED"
                            ? "success"
                            : "error"
                        }
                        label={
                          activeDpo?.status === "DPO_UPLOADED"
                            ? "MAPPED"
                            : "NOT MAPPED"
                        }
                      />
                      <Chip
                        size="small"
                        label={`Items: ${filteredDpoItems.length}`}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Search PartNo / Item"
                        value={dpoItemSearch}
                        onChange={(e) => setDpoItemSearch(e.target.value)}
                        InputProps={{ endAdornment: dpoItemSearchAdornment }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDpoItemSearch("")}
                        disabled={!dpoItemSearch}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Clear
                      </Button>
                    </Stack>

                    <Box sx={{ height: 520 }}>
                      <DataGrid
                        rows={filteredDpoItems}
                        getRowId={(r) => r.__rid}
                        columns={dpoItemCols}
                        density="compact"
                        rowHeight={32}
                        disableRowSelectionOnClick
                        loading={dpoItemsLoading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{ toolbar: { showQuickFilter: false } }}
                      />
                    </Box>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </Box>
        }
      />
    </div>
  );
};

export default DpoDashboard;
